import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import type { 
  PaymentBatchRequest,
  PaymentBatch,
  BulkEarningRequest
} from '../../types/Insurance';

// Payment batch processing
export const usePaymentBatches = (filters?: {
  provider?: string;
  status?: string;
  fromDate?: Date;
  toDate?: Date;
  currency?: string;
  limit?: number;
  offset?: number;
}) => {
  return useQuery({
    queryKey: ['payment-batches', filters],
    queryFn: () => apiClient.getPaymentBatches(filters),
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchOnWindowFocus: false
  });
};

export const useCreatePaymentBatch = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: PaymentBatchRequest): Promise<PaymentBatch> => apiClient.createPaymentBatch(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-batches'] });
      // Cross-invalidate ledger caches
      queryClient.invalidateQueries({ queryKey: ['ledger', 'journals'] });
      queryClient.invalidateQueries({ queryKey: ['ledger', 'reports'] });
    },
    onError: (error) => {
      console.error('Failed to create payment batch:', error);
    }
  });
};

export const useProcessBatch = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (batchId: string) => apiClient.processBatch(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-batches'] });
      queryClient.invalidateQueries({ queryKey: ['client-balance-summary'] });
      queryClient.invalidateQueries({ queryKey: ['reconciliation-report'] });
      // Cross-invalidate ledger caches
      queryClient.invalidateQueries({ queryKey: ['ledger', 'journals'] });
      queryClient.invalidateQueries({ queryKey: ['ledger', 'reports'] });
      queryClient.invalidateQueries({ queryKey: ['ledger', 'dashboard'] });
    },
    onError: (error) => {
      console.error('Failed to process batch:', error);
    }
  });
};

export const useBatchReconciliation = (batchId: string) => {
  return useQuery({
    queryKey: ['batch-reconciliation', batchId],
    queryFn: () => apiClient.getBatchReconciliation(batchId),
    enabled: !!batchId,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false
  });
};

// Premium earning automation
export const usePremiumEarningCandidates = (filters?: {
  organizationId?: string;
  currency?: string;
  minAmount?: string;
  maxAmount?: string;
}) => {
  return useQuery({
    queryKey: ['premium-earning-candidates', filters],
    queryFn: () => apiClient.getPremiumEarningCandidates(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false
  });
};

export const useProcessBulkPremiumEarning = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: BulkEarningRequest) => apiClient.processBulkPremiumEarning(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['premium-earning-candidates'] });
      queryClient.invalidateQueries({ queryKey: ['client-balance-summary'] });
      // Cross-invalidate ledger caches
      queryClient.invalidateQueries({ queryKey: ['ledger', 'journals'] });
      queryClient.invalidateQueries({ queryKey: ['ledger', 'reports'] });
      queryClient.invalidateQueries({ queryKey: ['ledger', 'dashboard'] });
    },
    onError: (error) => {
      console.error('Failed to process bulk premium earning:', error);
    }
  });
};

// Reconciliation reporting
export const useReconciliationReport = (filters?: {
  periodId?: string;
  organizationId?: string;
}) => {
  return useQuery({
    queryKey: ['reconciliation-report', filters],
    queryFn: () => apiClient.getReconciliationReport(filters),
    enabled: !!filters?.periodId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false
  });
};

// Commission tracking
export const useCommissionSummary = (filters?: {
  agentId?: string;
  currency?: string;
  status?: string;
  fromDate?: string;
  toDate?: string;
}) => {
  return useQuery({
    queryKey: ['commission-summary', filters],
    queryFn: () => apiClient.getCommissionSummary(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false
  });
};
